import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { getApiErrorMessage } from '@/lib/utils';
import { attendancesApi, attendancesQueryKeys } from '../api/attendances.api';
import type { ArriveDto, LeaveDto } from '../types';

export function ArriveButton() {
  const [loading, setLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [dialogType, setDialogType] = React.useState<'come' | 'leave' | null>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Check if user is currently inside
  const { data: isActive, refetch: refetchActive } = useQuery({
    queryKey: attendancesQueryKeys.active(),
    queryFn: async () => {
      const result = await attendancesApi.getActive();
      return !!result;
    },
    retry: 1,
  });

  const arriveMutation = useMutation({
    mutationFn: (data: ArriveDto) => attendancesApi.arrive(data),
    onSuccess: () => {
      toast.success(t('toast.dataSaved'));
      refetchActive();
      queryClient.invalidateQueries({ queryKey: attendancesQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('toast.error')));
    },
    onSettled: () => {
      setLoading(false);
      setShowDialog(false);
      setReason('');
      setDialogType(null);
    },
  });

  const leaveMutation = useMutation({
    mutationFn: (data: LeaveDto) => attendancesApi.leave(data),
    onSuccess: () => {
      toast.success(t('toast.dataSaved'));
      refetchActive();
      queryClient.invalidateQueries({ queryKey: attendancesQueryKeys.all });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('toast.error')));
    },
    onSettled: () => {
      setLoading(false);
      setShowDialog(false);
      setReason('');
      setDialogType(null);
    },
  });

  const getLocationAndSend = (reasonToSend: string | null, isLeave = false) => {
    if (isLeave) {
      setLoading(true);
      leaveMutation.mutate({
        early_reason: reasonToSend || undefined,
      });
      return;
    }

    if (!navigator.geolocation) {
      toast.error(t('attendance.geolocationNotSupported'));
      setLoading(false);
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        arriveMutation.mutate({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          late_reason: reasonToSend || undefined,
        });
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error(t('attendance.failedToGetLocation'));
        setLoading(false);
      }
    );
  };

  const handleHasComeClick = () => {
    const now = new Date();
    const nineAM = new Date();
    nineAM.setHours(9, 0, 0, 0);

    if (now > nineAM) {
      setDialogType('come');
      setShowDialog(true);
    } else {
      getLocationAndSend(null, false);
    }
  };

  const handleHasLeftClick = () => {
    const now = new Date();
    const sixPM = new Date();
    sixPM.setHours(18, 0, 0, 0);

    if (now < sixPM) {
      setDialogType('leave');
      setShowDialog(true);
    } else {
      getLocationAndSend(null, true);
    }
  };

  const handleDialogConfirm = () => {
    setShowDialog(false);
    if (dialogType === 'come') {
      getLocationAndSend(reason, false);
    } else if (dialogType === 'leave') {
      getLocationAndSend(reason, true);
    }
  };

  const handleDialogCancel = () => {
    setShowDialog(false);
    setReason('');
    setDialogType(null);
  };

  return (
    <div>
      {isActive ? (
        <Button
          onClick={handleHasLeftClick}
          variant="destructive"
          className="text-white"
          disabled={loading}
        >
          {loading ? t('saving') : t('attendance.hasLeft')}
        </Button>
      ) : (
        <Button
          onClick={handleHasComeClick}
          className="bg-[#001638] hover:bg-[#002255] text-white"
          disabled={loading}
        >
          {loading ? t('saving') : t('attendance.hasCome')}
        </Button>
      )}

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialogType === 'come'
                ? t('attendance.lateArrivalReasonTitle')
                : t('attendance.earlyLeaveReasonTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {dialogType === 'come'
                ? t('attendance.lateArrivalReasonDesc')
                : t('attendance.earlyLeaveReasonDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                dialogType === 'come'
                  ? t('attendance.lateArrivalReasonPlaceholder')
                  : t('attendance.earlyLeaveReasonPlaceholder')
              }
              rows={4}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDialogCancel}>
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDialogConfirm}
              disabled={loading || reason.trim() === ''}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t('actions.submit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
